import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Input,
  useToast,
} from '@biartbt/biartd'
import {
  GripVertical,
  ChevronUp,
  ChevronDown,
  Plus,
  Edit,
  Trash2,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react'
import * as LucideIcons from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { menuAPI, type MenuItem } from '@/services/menuAPI'
import { useActiveTenantId } from '@/store/tenantStore'

const APP_ROUTES = [
  '/dashboard',
  '/system/organizations',
  '/system/organization-types',
  '/system/data-templates',
  '/system/nace-codes',
  '/system/tenants',
  '/system/employment-statuses',
  '/system/occupations',
  '/system/cost-centers',
  '/system/department-templates',
  '/system/position-templates',
  '/system/nace-class-codes',
  '/system/menus',
  '/organization/option-defs/employee-types',
  '/organization/option-defs/license-types',
  '/organization/option-defs/military-status',
  '/organization/option-defs/job-definitions',
  '/organization/option-defs/personnel-statuses',
]

const LUCIDE_ICON_NAMES = [
  'Settings', 'Shield', 'Database', 'Code', 'Building2', 'Users', 'BarChart3', 'Home', 'Menu',
  'Edit', 'Trash2', 'Plus', 'Search', 'Bell', 'Mail', 'Heart', 'Star', 'Download', 'Upload',
  'Share2', 'Lock', 'Unlock', 'Eye', 'EyeOff', 'AlertCircle', 'CheckCircle', 'XCircle', 'Info',
  'HelpCircle', 'Briefcase', 'Folder', 'FileText', 'Calendar', 'Clock', 'MapPin', 'Phone',
  'Globe', 'Zap', 'TrendingUp', 'PieChart', 'LayoutDashboard',
]

type MenuItemWithMeta = MenuItem & { $new?: boolean }

function getLucideIcon(iconName?: string): React.ComponentType<{ className?: string }> | null {
  if (!iconName) return null
  const Icon = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[iconName]
  return Icon || null
}

function routeToLabel(route: string): string {
  const parts = route.split('/').filter(Boolean)
  const last = parts[parts.length - 1] ?? route
  return last
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function collectRoutes(items: MenuItemWithMeta[]): Set<string> {
  const set = new Set<string>()
  const walk = (list: MenuItemWithMeta[]) => {
    list.forEach((item) => {
      if (item.route) set.add(item.route)
      if (item.children?.length) walk(item.children as MenuItemWithMeta[])
    })
  }
  walk(items)
  return set
}

function calculateSortOrders(items: MenuItemWithMeta[]): MenuItemWithMeta[] {
  return items.map((item, index) => ({
    ...item,
    sortOrder: index + 1,
    children: item.children?.length
      ? calculateSortOrders(item.children as MenuItemWithMeta[])
      : [],
  }))
}

function flattenTree(
  items: MenuItemWithMeta[],
  parentId?: number | null
): MenuItemWithMeta[] {
  const out: MenuItemWithMeta[] = []
  items.forEach((item) => {
    out.push({ ...item, parentId: parentId ?? undefined })
    if (item.children?.length) {
      out.push(...flattenTree(item.children as MenuItemWithMeta[], item.id))
    }
  })
  return out
}

function findMenuById(items: MenuItemWithMeta[], id: number): MenuItemWithMeta | undefined {
  for (const item of items) {
    if (item.id === id) return item
    if (item.children?.length) {
      const found = findMenuById(item.children as MenuItemWithMeta[], id)
      if (found) return found
    }
  }
  return undefined
}

export default function MenusPage() {
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const activeTenantId = useActiveTenantId()
  const tenantId = useMemo(() => {
    const fromUrl = searchParams.get('tenantId')
    if (fromUrl) {
      const n = parseInt(fromUrl, 10)
      if (!Number.isNaN(n)) return n
    }
    return activeTenantId ?? 1
  }, [searchParams, activeTenantId])

  const [menus, setMenus] = useState<MenuItemWithMeta[]>([])
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingMenu, setEditingMenu] = useState<MenuItemWithMeta | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const { data: treeData = [], isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['menus-tree-admin', tenantId],
    queryFn: () => menuAPI.getTree(tenantId, undefined),
    staleTime: 0,
  })

  useEffect(() => {
    setMenus(Array.isArray(treeData) ? (treeData as MenuItemWithMeta[]) : [])
  }, [treeData])

  useEffect(() => {
    if (queryError) setError(queryError instanceof Error ? queryError.message : String(queryError))
  }, [queryError])

  const missingMenus = useMemo(() => {
    const inMenu = collectRoutes(menus)
    return APP_ROUTES.filter((r) => !inMenu.has(r)).map((route) => ({
      route,
      label: routeToLabel(route),
    }))
  }, [menus])

  const updateInTree = useCallback((id: number, field: keyof MenuItemWithMeta, value: unknown) => {
    setMenus((prev) => {
      const update = (list: MenuItemWithMeta[]): MenuItemWithMeta[] =>
        list.map((item) => {
          if (item.id === id) return { ...item, [field]: value }
          if (item.children?.length) {
            return { ...item, children: update(item.children as MenuItemWithMeta[]) }
          }
          return item
        })
      return calculateSortOrders(update(prev))
    })
  }, [])

  const handleSaveAll = useCallback(async () => {
    setSaving(true)
    setError('')
    try {
      const flat = flattenTree(menus)
      for (const item of flat) {
        const payload = {
          label: item.label,
          description: item.description ?? undefined,
          icon: item.icon ?? undefined,
          route: item.route,
          parentId: item.parentId ?? undefined,
          sortOrder: item.sortOrder,
          isActive: item.isActive,
        }
        if (item.$new) {
          await menuAPI.create({ ...payload, tenantId })
        } else {
          await menuAPI.update(item.id, payload)
        }
      }
      setSuccess('Menüler başarıyla kaydedildi')
      setTimeout(() => setSuccess(''), 3000)
      setIsEditMode(false)
      refetch()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Kayıt başarısız'
      setError(msg)
      toast({ title: 'Hata', description: msg, variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }, [menus, tenantId, refetch, toast])

  const openEditModal = (item: MenuItemWithMeta) => {
    setEditingMenu({ ...item })
    setModalOpen(true)
  }

  const saveModal = async () => {
    if (!editingMenu) return
    if (editingMenu.id > 0) {
      try {
        await menuAPI.update(editingMenu.id, {
          label: editingMenu.label,
          description: editingMenu.description ?? undefined,
          icon: editingMenu.icon ?? undefined,
          route: editingMenu.route,
          parentId: editingMenu.parentId ?? undefined,
          sortOrder: editingMenu.sortOrder,
          isActive: editingMenu.isActive,
        })
        setSuccess('Menü güncellendi')
        setTimeout(() => setSuccess(''), 3000)
        setModalOpen(false)
        setEditingMenu(null)
        refetch()
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Güncelleme başarısız'
        toast({ title: 'Hata', description: msg, variant: 'danger' })
      }
    } else {
      setMenus((prev) => {
        const applyEdit = (list: MenuItemWithMeta[]): MenuItemWithMeta[] =>
          list.map((it) =>
            it.id === editingMenu.id
              ? {
                  ...it,
                  label: editingMenu.label,
                  description: editingMenu.description,
                  icon: editingMenu.icon,
                  route: editingMenu.route,
                  isActive: editingMenu.isActive,
                }
              : it.children?.length
                ? { ...it, children: applyEdit(it.children as MenuItemWithMeta[]) }
                : it
          )
        return applyEdit(prev)
      })
      setModalOpen(false)
      setEditingMenu(null)
    }
  }

  const addChild = (parentId: number) => {
    const newItem: MenuItemWithMeta = {
      id: -Date.now(),
      label: 'Yeni menü',
      route: '',
      sortOrder: 0,
      isActive: true,
      children: [],
      $new: true,
    }
    setMenus((prev) => {
      const add = (list: MenuItemWithMeta[]): MenuItemWithMeta[] =>
        list.map((item) => {
          if (item.id === parentId) {
            const children = [...(item.children || [])] as MenuItemWithMeta[]
            children.push(newItem)
            return { ...item, children }
          }
          if (item.children?.length) {
            return { ...item, children: add(item.children as MenuItemWithMeta[]) }
          }
          return item
        })
      return calculateSortOrders(add(prev))
    })
  }

  const removeFromTree = useCallback((id: number) => {
    setMenus((prev) => {
      const filter = (list: MenuItemWithMeta[]): MenuItemWithMeta[] =>
        list
          .filter((item) => item.id !== id)
          .map((item) => ({
            ...item,
            children: item.children?.length
              ? filter(item.children as MenuItemWithMeta[])
              : [],
          }))
      return calculateSortOrders(filter(prev))
    })
  }, [])

  const handleDeleteClick = useCallback(
    async (item: MenuItemWithMeta) => {
      if (item.$new) {
        removeFromTree(item.id)
        return
      }
      if (!window.confirm('Bu menüyü silmek istediğinize emin misiniz? Alt menüler etkilenebilir.')) return
      setSaving(true)
      try {
        await menuAPI.delete(item.id)
        setSuccess('Menü silindi')
        setTimeout(() => setSuccess(''), 3000)
        const result = await refetch()
        if (result?.data) setMenus(Array.isArray(result.data) ? (result.data as MenuItemWithMeta[]) : [])
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Silme başarısız'
        toast({ title: 'Hata', description: msg, variant: 'danger' })
      } finally {
        setSaving(false)
      }
    },
    [removeFromTree, refetch, toast]
  )

  const moveUp = (id: number) => {
    setMenus((prev) => {
      const moveRecursive = (items: MenuItemWithMeta[]): MenuItemWithMeta[] => {
        const index = items.findIndex((item) => item.id === id)
        if (index > 0) {
          const newItems = [...items]
          ;[newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]]
          return calculateSortOrders(newItems)
        }
        return items.map((item) =>
          item.children?.length
            ? { ...item, children: moveRecursive(item.children as MenuItemWithMeta[]) }
            : item
        )
      }
      return moveRecursive(prev)
    })
  }

  const moveDown = (id: number) => {
    setMenus((prev) => {
      const moveInList = (items: MenuItemWithMeta[]): { done: boolean; result: MenuItemWithMeta[] } => {
        const index = items.findIndex((item) => item.id === id)
        if (index >= 0 && index < items.length - 1) {
          const newItems = [...items]
          ;[newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]]
          return { done: true, result: calculateSortOrders(newItems) }
        }
        for (let i = 0; i < items.length; i++) {
          if (items[i].children?.length) {
            const child = moveInList(items[i].children as MenuItemWithMeta[])
            if (child.done) {
              const newItems = [...items]
              newItems[i] = { ...items[i], children: child.result }
              return { done: true, result: newItems }
            }
          }
        }
        return { done: false, result: items }
      }
      const out = moveInList(prev)
      return out.done ? out.result : prev
    })
  }

  const indentMenu = (id: number) => {
    setMenus((prev) => {
      const indentRecursive = (items: MenuItemWithMeta[]): MenuItemWithMeta[] => {
        const index = items.findIndex((item) => item.id === id)
        if (index > 0) {
          const newItems = [...items]
          const itemToIndent = newItems[index]
          const parentItem = newItems[index - 1]
          const child: MenuItemWithMeta = { ...itemToIndent, parentId: parentItem.id }
          newItems.splice(index, 1)
          const parentIndex = index - 1
          const parent = newItems[parentIndex]
          const children = [...(parent.children || [])] as MenuItemWithMeta[]
          children.push(child)
          newItems[parentIndex] = { ...parent, children }
          return calculateSortOrders(newItems)
        }
        return items.map((item) =>
          item.children?.length
            ? { ...item, children: indentRecursive(item.children as MenuItemWithMeta[]) }
            : item
        )
      }
      return indentRecursive(prev)
    })
  }

  const outdentMenu = (id: number) => {
    const item = findMenuById(menus, id)
    if (!item) return
    const itemParentId = item.parentId
    let grandparentId: number | undefined
    const findGrandparent = (items: MenuItemWithMeta[]): boolean => {
      for (const m of items) {
        if (m.id === itemParentId) {
          grandparentId = m.parentId
          return true
        }
        if (m.children?.length && findGrandparent(m.children as MenuItemWithMeta[])) return true
      }
      return false
    }
    findGrandparent(menus)

    setMenus((prev) => {
      let outdentedItem: MenuItemWithMeta | null = null
      const outdentRecursive = (items: MenuItemWithMeta[]): MenuItemWithMeta[] => {
        const result: MenuItemWithMeta[] = []
        for (const item of items) {
          if (item.id === id) {
            outdentedItem = { ...item, parentId: grandparentId }
            continue
          }
          if (item.children?.length) {
            result.push({ ...item, children: outdentRecursive(item.children as MenuItemWithMeta[]) })
          } else {
            result.push(item)
          }
        }
        return result
      }
      let newMenus = outdentRecursive(prev)
      if (outdentedItem) {
        if (grandparentId !== undefined) {
          const addToParent = (items: MenuItemWithMeta[]): MenuItemWithMeta[] =>
            items.map((it) => {
              if (it.id === grandparentId) {
                return { ...it, children: [...(it.children || []), outdentedItem!] as MenuItemWithMeta[] }
              }
              if (it.children?.length) return { ...it, children: addToParent(it.children as MenuItemWithMeta[]) }
              return it
            })
          newMenus = addToParent(newMenus)
        } else {
          newMenus = [...newMenus, outdentedItem]
        }
      }
      return calculateSortOrders(newMenus)
    })
  }

  const addMissingToTree = (route: string, label: string) => {
    const newItem: MenuItemWithMeta = {
      id: -Date.now(),
      label,
      route,
      sortOrder: menus.length + 1,
      isActive: true,
      children: [],
      $new: true,
    }
    setMenus(calculateSortOrders([...menus, newItem]))
    setIsEditMode(true)
  }

  function renderMenuRow(item: MenuItemWithMeta, depth: number) {
    const IconComponent = getLucideIcon(item.icon)
    return (
      <React.Fragment key={item.id}>
        <div
          className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 hover:bg-gray-50"
          style={{ paddingLeft: 16 + depth * 24 }}
        >
          {isEditMode && (
            <div className="flex-shrink-0 w-6 text-gray-400 cursor-move">
              <GripVertical className="w-4 h-4" />
            </div>
          )}
          {!isEditMode && <div className="flex-shrink-0 w-6" />}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-gray-600">
              {IconComponent ? <IconComponent className="w-4 h-4" /> : <span className="text-gray-400 text-xs">-</span>}
            </div>
            {isEditMode ? (
              <Input
                value={item.label || ''}
                onChange={(e) => updateInTree(item.id, 'label', e.target.value)}
                placeholder="Label"
                className="flex-1 min-w-32 h-8 text-sm"
              />
            ) : (
              <div className="flex-1 min-w-32 h-8 text-sm flex items-center px-2 rounded border border-transparent bg-transparent text-gray-700 dark:text-gray-300">
                {item.label || '—'}
              </div>
            )}
          </div>
          {isEditMode ? (
            <Input
              value={item.description || ''}
              onChange={(e) => updateInTree(item.id, 'description', e.target.value)}
              placeholder="Açıklama"
              className="flex-1 min-w-32 h-8 text-sm"
            />
          ) : (
            <div className="flex-1 min-w-32 h-8 text-sm flex items-center px-2 rounded border border-transparent bg-transparent text-gray-700 dark:text-gray-300 truncate">
              {item.description || '—'}
            </div>
          )}
          {isEditMode ? (
            <Input
              value={item.route || ''}
              onChange={(e) => updateInTree(item.id, 'route', e.target.value)}
              placeholder="Route"
              className="flex-1 min-w-40 h-8 text-sm"
            />
          ) : (
            <div className="flex-1 min-w-40 h-8 text-sm flex items-center px-2 rounded border border-transparent bg-transparent text-gray-700 dark:text-gray-300 truncate">
              {item.route || '—'}
            </div>
          )}
          <div className="flex gap-1 flex-shrink-0 items-center" style={{ minWidth: 280, justifyContent: 'center' }}>
            {isEditMode && (
              <>
                <Button variant="ghost" size="sm" onClick={() => openEditModal(item)} title="Düzenle">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => moveUp(item.id)} title="Yukarı">
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => moveDown(item.id)} title="Aşağı">
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => indentMenu(item.id)} title="İçeri">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => outdentMenu(item.id)} title="Dışarı">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => addChild(item.id)} title="Alt menü">
                  <Plus className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(item)} title="Sil">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
        {(item.children as MenuItemWithMeta[])?.length
          ? (item.children as MenuItemWithMeta[]).map((child) => renderMenuRow(child, depth + 1))
          : null}
      </React.Fragment>
    )
  }

  return (
    <div className="p-6 h-full flex flex-col bg-gray-50 dark:bg-gray-900/50">
      {/* HEADER - Pearl style */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Menüler</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isEditMode
              ? 'Menüleri düzenle - Ok butonlarıyla sırala, içeri/dışarı taşı'
              : 'Sistem menülerini yönet'}
          </p>
        </div>
        <div className="flex gap-2">
          {!isEditMode ? (
            <Button onClick={() => setIsEditMode(true)}>Güncelle</Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsEditMode(false)
                  refetch()
                }}
              >
                Vazgeç
              </Button>
              <Button onClick={handleSaveAll} disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ERROR / SUCCESS - Pearl style */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex justify-between items-start gap-4">
          <div className="flex-1 whitespace-pre-wrap text-sm">{error}</div>
          <Button onClick={() => setError('')} variant="ghost" size="sm">×</Button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 flex justify-between items-center">
          <span>{success}</span>
          <Button onClick={() => setSuccess('')} variant="ghost" size="sm">×</Button>
        </div>
      )}

      {/* MISSING MENUS - Pearl style */}
      {!isLoading && missingMenus.length > 0 && (
        <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <h3 className="font-bold text-yellow-900 dark:text-yellow-200 mb-3">Menüde Olmayan Sayfalar ({missingMenus.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {missingMenus.map((missing) => (
              <div
                key={missing.route}
                className="flex items-center justify-between bg-white dark:bg-gray-800 p-2 rounded border border-yellow-100 dark:border-yellow-800"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{missing.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{missing.route}</p>
                </div>
                <Button
                  size="sm"
                  className="ml-2 flex-shrink-0"
                  onClick={() => addMissingToTree(missing.route, missing.label)}
                >
                  Ekle
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STICKY TABLE HEADER - Pearl style */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700 z-10 rounded-t-lg shadow-sm">
        <div className="flex items-center gap-2 px-4 py-3 font-bold text-sm bg-gray-100 dark:bg-gray-700/50 rounded-t-lg">
          <div className="flex-shrink-0 w-6" />
          <div className="flex items-center gap-2 flex-1 min-w-32">
            <div className="w-6 h-6 flex-shrink-0" />
            <span className="flex-1">Label</span>
          </div>
          <div className="flex-1 min-w-32">Açıklama</div>
          <div className="flex-1 min-w-40">Route</div>
          <div className="flex-shrink-0 text-center" style={{ minWidth: 280 }}>İşlemler</div>
        </div>
      </div>

      {/* CONTENT - list of rows */}
      <Card className="flex-1 rounded-t-none overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12 text-gray-500">Yükleniyor...</div>
        ) : menus.length === 0 ? (
          <div className="flex justify-center items-center py-12 text-gray-500">Menü bulunamadı</div>
        ) : (
          <div className="bg-white dark:bg-gray-800">
            {menus.map((item) => renderMenuRow(item, 0))}
          </div>
        )}
      </Card>

      {/* MODAL - Pearl style */}
      {modalOpen && editingMenu && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Menü Detayı</h2>
              <Button
                variant="ghost"
                size="sm"
                className="text-2xl h-8 w-8 p-0"
                onClick={() => { setModalOpen(false); setEditingMenu(null) }}
              >
                ×
              </Button>
            </div>
            <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Label</label>
                <Input
                  value={editingMenu.label || ''}
                  onChange={(e) => setEditingMenu((prev) => (prev ? { ...prev, label: e.target.value } : null))}
                  placeholder="Label"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Route</label>
                  <Input
                    value={editingMenu.route || ''}
                    onChange={(e) => setEditingMenu((prev) => (prev ? { ...prev, route: e.target.value } : null))}
                    placeholder="/system/organization-types"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">İkon</label>
                  <div className="flex gap-2 items-center">
                    <select
                      value={editingMenu.icon || ''}
                      onChange={(e) => setEditingMenu((prev) => (prev ? { ...prev, icon: e.target.value || undefined } : null))}
                      className="flex-1 h-9 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seç</option>
                      {LUCIDE_ICON_NAMES.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    {editingMenu.icon && (() => {
                      const Icon = getLucideIcon(editingMenu.icon)
                      return (
                        <div className="w-10 h-10 flex items-center justify-center border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700">
                          {Icon ? <Icon className="w-5 h-5" /> : <span className="text-gray-400 text-xs">-</span>}
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Açıklama</label>
                <textarea
                  value={editingMenu.description || ''}
                  onChange={(e) => setEditingMenu((prev) => (prev ? { ...prev, description: e.target.value || undefined } : null))}
                  className="w-full min-h-[60px] px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Açıklama"
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="modal-active"
                  checked={editingMenu.isActive ?? true}
                  onChange={(e) => setEditingMenu((prev) => (prev ? { ...prev, isActive: e.target.checked } : null))}
                  className="rounded border-gray-300"
                />
                <label htmlFor="modal-active" className="text-sm font-medium text-gray-700 dark:text-gray-300">Aktif</label>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
              <Button variant="secondary" onClick={() => { setModalOpen(false); setEditingMenu(null) }}>
                Kapat
              </Button>
              <Button onClick={saveModal} disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
